/* The order record modal — `OrderDetail` in the app's wide `dp-modal`, with its own loading/error
   states and two footer actions (copy the order's link, open its page). Moved out of `List.js`
   unchanged when the counterparty card's Orders tab needed the same modal: one definition, two
   callers, rather than a second copy of the markup drifting from the first.

   Deliberately not URL-integrated: the address bar stays where the caller is while this is open.
   What differs per caller is passed in, not branched on here:
     `openTo`           where "Open page" goes — the list carries its filtered query string, the
                        card its own address in route state, so each page's Back returns to it;
     `navigate`         how a link inside the record navigates (the card asks before leaving
                        unsaved edits); defaults to `history.push`;
     `hideCounterparty` drops the "Counterparty" row — redundant on that counterparty's own card.

   Esc and a mask click both close it — antd's own defaults (`keyboard`, `maskClosable`), left
   untouched. A modal, not a drawer: the app's own convention (`visit/parts.js`'s `RecordModal`,
   `points/ext/AddToGroup.js`). `width={1000}` reuses `RecordModal`'s "wide" value, picked there for
   the same reason — a record with a line-items table. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert as AntAlert, Button, Flex, Modal, Skeleton } from "antd";
import { CopyOutlined, ExpandOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import { ORDERS_PAGE } from "../../config/lang";
import { call, isOk } from "../../util/action";
import { translate } from "../../util/util";

import OrderDetail, { OrderDetailHead } from "./OrderDetail";

export default function OrderModal({ id, lang, onClose, openTo, navigate, hideCounterparty = false }) {
  const history = useHistory();
  const t = key => translate(ORDERS_PAGE, key, lang);
  const go = navigate || (to => history.push(to));

  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | error | ready
  /* `call()` cannot be aborted: only the newest request may land, so closing one record and opening
     another quickly never shows the first one's answer under the second's footer. */
  const request = useRef(0);

  const load = useCallback(() => {
    if (!id) return;
    const mine = ++request.current;
    setStatus("loading");
    setItem(null);
    call({ method: "GET", url: `excalibur/orders/${id}/` }, response => {
      if (mine !== request.current) return;
      if (!isOk(response) || !response?.id) return setStatus("error");
      setItem(response);
      setStatus("ready");
    });
  }, [id]);

  useEffect(load, [load]);
  /* Reopened on another record, the first frame still holds the previous one — not shown. */
  const ready = status === "ready" && item && String(item.id) === String(id);

  const copyLink = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(`${window.location.origin}/sales/orders/${id}`).catch(() => {});
    Alert.success(t("link_copied"), { position: "top-right" });
  };

  return (
    <Modal
      open={Boolean(id)}
      onCancel={onClose}
      width={1000}
      destroyOnHidden
      className="dp-modal"
      title={ready ? <OrderDetailHead item={item} lang={lang} hideCounterparty={hideCounterparty} /> : t("list_title")}
      footer={
        <Flex justify="space-between" align="center" gap="small" wrap>
          <Button icon={<CopyOutlined />} onClick={copyLink}>
            {t("copy_link_btn")}
          </Button>
          <Button icon={<ExpandOutlined />} onClick={() => go(openTo || `/sales/orders/${id}`)}>
            {t("open_page_btn")}
          </Button>
        </Flex>
      }>
      {status === "error" ? (
        <AntAlert
          type="error"
          showIcon
          message={t("error_title")}
          action={
            <Button size="small" onClick={load}>
              {t("retry")}
            </Button>
          }
        />
      ) : !ready ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (
        <Flex vertical gap="middle">
          <OrderDetail item={item} lang={lang} showHeader={false} navigate={navigate} hideCounterparty={hideCounterparty} />
        </Flex>
      )}
    </Modal>
  );
}
